import { useEffect, useRef, useCallback } from 'react';
import { performanceMonitor } from '../lib/performance';
import { logger } from '../lib/logger';

export const usePerformance = (componentName: string) => {
  const renderStartTime = useRef<number>(performance.now());
  const mountTime = useRef<number | null>(null);

  // Track component mount time
  useEffect(() => {
    mountTime.current = performance.now();
    const mountDuration = mountTime.current - renderStartTime.current;
    
    performanceMonitor.recordMetric(
      'component_mount_time',
      mountDuration,
      `Component: ${componentName}`
    );

    // Warn about slow components
    if (mountDuration > 100) {
      logger.warn(
        `Slow component mount: ${componentName} took ${mountDuration.toFixed(2)}ms`,
        'Performance'
      );
    }

    return () => {
      if (mountTime.current) {
        const unmountTime = performance.now();
        const lifetimeDuration = unmountTime - mountTime.current;
        
        performanceMonitor.recordMetric(
          'component_lifetime',
          lifetimeDuration,
          `Component: ${componentName}`
        );
      }
    };
  }, [componentName]);

  // Measure async operations
  const measureAsync = useCallback(async <T>(
    operation: () => Promise<T>,
    operationName: string
  ): Promise<T> => {
    const startTime = performance.now();
    
    try {
      const result = await operation();
      const duration = performance.now() - startTime;
      
      performanceMonitor.recordMetric(
        'async_operation_time',
        duration,
        `${componentName}: ${operationName}`
      );

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      performanceMonitor.recordMetric(
        'async_operation_error_time',
        duration,
        `${componentName}: ${operationName} (Error)`
      );
      
      throw error;
    }
  }, [componentName]);

  // Measure render performance
  const measureRender = useCallback((renderName: string) => {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric(
        'render_time',
        duration,
        `${componentName}: ${renderName}`
      );
    };
  }, [componentName]);

  return {
    measureAsync,
    measureRender
  };
};